import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTop10, useNewReleases } from '../hooks/useHome';
import { toTitles } from '../lib/catalogue';
import { TitleCard } from '../components/title-card';
import { PageMasthead } from '../components/page-masthead';
import { SubscribeBand } from '../components/subscribe-band';
import { TitleMeta } from '../components/title-meta';
import { Icon } from '@iconify/react';

export default function PopularPage() {
  usePageTitle(
    'Popular · Kast',
    'The top 10 most watched movies and series on Kast right now, plus what is trending this week.'
  );
  const navigate = useNavigate();
  const { data: rawTop10 = [], isLoading: isTop10Loading } = useTop10();
  const { data: rawTrending = [], isLoading: isTrendingLoading } = useNewReleases(10);

  const top10Titles = useMemo(() => toTitles(rawTop10), [rawTop10]);
  const trendingTitles = useMemo(() => toTitles(rawTrending), [rawTrending]);

  return (
    <div className="flex flex-col min-h-screen">
      <PageMasthead
        title="Popular on Kast"
        lead="The top 10 most watched movies and series right now, plus trending titles this week."
        meta={
          <TitleMeta
            className="text-sm"
            items={['Leaderboard', 'Updated Hourly', 'Top 10 Worldwide']}
          />
        }
      />

      <div className="mx-auto max-w-[1600px] w-full gutter pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Column: Ranked Top 10 */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-6">
              <Icon icon="ph:trophy-bold" className="size-5 text-amber-400" />
              <h2 className="text-xl font-bold tracking-tight text-white display">Top 10 Today</h2>
            </div>

            {isTop10Loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[2/3] rounded-xl bg-white/5 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {top10Titles.map((title, idx) => (
                  <TitleCard key={title.id} title={title} rank={idx + 1} />
                ))}
              </div>
            )}
          </div>

          {/* Side Column: Trending List */}
          <div className="lg:col-span-4 border-t border-white/8 pt-8 lg:border-t-0 lg:pt-0 lg:border-l lg:border-white/8 lg:pl-8">
            <div className="flex items-center gap-2 mb-6">
              <Icon icon="ph:trend-up-bold" className="size-5 text-primary" />
              <h2 className="text-xl font-bold tracking-tight text-white display">Trending Releases</h2>
            </div>

            <div className="flex flex-col gap-3">
              {isTrendingLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
                ))
              ) : (
                trendingTitles.map((title, idx) => (
                  <button
                    key={`trend-${title.id}`}
                    type="button"
                    onClick={() => navigate(`/movie/${title.id}`)}
                    className="flex items-center gap-3.5 rounded-xl border border-white/6 bg-white/[0.02] p-2.5 text-left transition-all duration-200 hover:border-white/15 hover:bg-white/[0.06] cursor-pointer group"
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center font-extrabold text-sm text-muted-foreground group-hover:text-primary">
                      {idx + 1}
                    </span>
                    <div className="relative aspect-video w-20 shrink-0 overflow-hidden rounded-lg bg-card">
                      {title.thumbnail ? (
                        <img
                          src={title.thumbnail}
                          alt={title.title}
                          className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex size-full items-center justify-center bg-white/5">
                          <Icon icon="ph:film-slate" className="size-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs sm:text-sm font-semibold truncate text-white group-hover:text-primary transition-colors">
                        {title.title}
                      </h3>
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                        {title.category || 'Featured'} {title.year ? `• ${title.year}` : ''}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <SubscribeBand />
    </div>
  );
}
